import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FaTag, FaCheck, FaPlus, FaArrowLeft, FaStar, FaCircle, FaBan, FaTimes, FaFolderOpen, FaChevronRight, FaChevronDown, FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import { productListApiService } from '../../../service/admin/productApiService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import Loader from '../../../component/common/loader';
import './TagProducts.css';

const STOP_WORDS = new Set([
  'a','an','the','and','or','of','in','on','at','to','for','is','are','with','by',
  'from','as','it','its','be','was','been','has','have','had','do','does','did',
  'will','would','could','should','may','might','shall','can','not','no','nor',
  'but','so','yet','both','either','neither','each','every','all','any','few',
  'more','most','other','some','such','than','too','very','just','only','also',
  'up','down','out','off','over','under','into','through','during','before',
  'after','above','below','between','within','without','along','across',
  'plus','except','x','set','pack','pcs','pc','cm','mm','inch','kg','gm','ml',
  'piece','pieces','i','s','new','hot','top','high','end','1','2','3','4','5',
  '6','7','8','9','0',
]);

function tokenizeTitle(title) {
  if (!title) return [];
  return title
    .replace(/[\/\-\(\)\[\],|&.]/g, ' ')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .map(w => w.trim().toLowerCase())
    .filter(w => w.length > 1 && !STOP_WORDS.has(w) && !/^\d+$/.test(w));
}

function HighlightedTitle({ title, selectedKeywords, existingKeywords }) {
  if (!title) return null;
  const selectedSet = new Set(selectedKeywords.map(w => w.toLowerCase()));
  const existingSet = new Set(existingKeywords.map(w => w.toLowerCase()));
  const parts = [];
  const regex = /([a-zA-Z0-9]+)|([^a-zA-Z0-9]+)/g;
  let match;
  while ((match = regex.exec(title)) !== null) {
    const word = match[0];
    const lower = word.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (lower && selectedSet.has(lower)) {
      parts.push(<mark key={match.index} className="tp-highlight-selected">{word}</mark>);
    } else if (lower && existingSet.has(lower)) {
      parts.push(<mark key={match.index} className="tp-highlight-existing">{word}</mark>);
    } else {
      parts.push(<span key={match.index}>{word}</span>);
    }
  }
  return <>{parts}</>;
}

const CLASSIFICATION_CONFIG = {
  important: { label: 'Important', icon: FaStar, color: 'green' },
  normal:    { label: 'Normal',    icon: FaCircle, color: 'blue' },
  block:     { label: 'Block',     icon: FaBan,    color: 'red' },
};

// Extract category ID: prefer _id (string or { $oid }); fallback to node_id (many nodes have no _id in API)
function getCategoryId(cat) {
  if (!cat) return '';
  const raw = cat._id !== undefined ? cat._id : cat.id;
  if (raw != null) {
    if (typeof raw === 'string') return raw.trim();
    if (typeof raw === 'object' && raw !== null && typeof raw.$oid === 'string') return raw.$oid.trim();
    if (typeof raw === 'object' && raw !== null && typeof raw.toString === 'function') return String(raw.toString());
    const s = String(raw);
    if (s && s !== '[object Object]') return s;
  }
  // API often has no _id on root or nested nodes; every node has node_id
  const nodeId = cat.node_id;
  if (nodeId != null) return `node_id:${nodeId}`;
  return '';
}

// Flatten tree to list of { _id, node_id, c_name, path } for search (node_id for fallback ID)
function flattenCategoryTree(nodes, parentPath = '') {
  const out = [];
  if (!Array.isArray(nodes)) return out;
  for (const node of nodes) {
    if (!node || node.hide) continue;
    const path = parentPath ? `${parentPath} > ${node.c_name}` : (node.c_name || '');
    out.push({ _id: node._id, node_id: node.node_id, c_name: node.c_name, path });
    if (Array.isArray(node.children) && node.children.length > 0) {
      out.push(...flattenCategoryTree(node.children, path));
    }
  }
  return out;
}

// Tree row: arrow = expand subcategories only; button = map to this category
function CategoryTreeRow({ node, depth, parentPath, expandedIds, onToggle, onSelect }) {
  if (!node || node.hide) return null;
  const id = node._id?.toString?.() ?? node._id;
  const path = parentPath ? `${parentPath} > ${node.c_name}` : (node.c_name || '');
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isExpanded = hasChildren && expandedIds.has(id);
  const children = (node.children || []).filter(c => !c.hide);

  return (
    <div className="tp-cat-tree-node">
      <div className="tp-cat-tree-row" style={{ paddingLeft: 12 + depth * 20 }}>
        {hasChildren ? (
          <button
            type="button"
            className="tp-cat-tree-expand-btn"
            onClick={(e) => { e.stopPropagation(); onToggle(id); }}
            title={isExpanded ? 'Collapse' : 'Expand subcategories'}
          >
            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
          </button>
        ) : (
          <span className="tp-cat-tree-no-expand" />
        )}
        <span className="tp-cat-tree-name">{node.c_name}</span>
        <button
          type="button"
          className="tp-cat-tree-map-btn"
          onClick={(e) => { e.stopPropagation(); onSelect({ _id: node._id, node_id: node.node_id, c_name: node.c_name, path }); }}
        >
          Map here
        </button>
      </div>
      {isExpanded && children.map((child) => (
        <CategoryTreeRow
          key={child._id}
          node={child}
          depth={depth + 1}
          parentPath={path}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

const TagProducts = () => {
  const { tag } = useParams();
  const [searchParams] = useSearchParams();
  const isParentMode = searchParams.get('parent') === 'true' || searchParams.get('parent') === '1';
  const message = useMessage();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [commonKeywordsFromApi, setCommonKeywordsFromApi] = useState(null);
  const [saving, setSaving] = useState(false);

  const productsPerPage = 25;

  const [selected, setSelected] = useState(new Set());
  const [showCountOneKeywords, setShowCountOneKeywords] = useState(false);

  // Local classification buckets: { important: Set, normal: Set, block: Set }
  const [classifiedLocal, setClassifiedLocal] = useState({ important: new Set(), normal: new Set(), block: new Set() });

  // Category mapping state
  const [showCategoryPicker, setShowCategoryPicker] = useState(null);
  const [categoryTree, setCategoryTree] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [expandedNodeIds, setExpandedNodeIds] = useState(new Set());
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [existingMappings, setExistingMappings] = useState([]);
  const [editMapping, setEditMapping] = useState(null);
  const [editKeywordInput, setEditKeywordInput] = useState('');

  const [customTagName, setCustomTagName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const fetchProducts = useCallback(async (page) => {
    setLoading(true);
    try {
      const includeKeywords = page === 1;
      const res = await productListApiService.getTagProducts(tag, page, productsPerPage, {
        parent: isParentMode,
        includeCommonKeywords: includeKeywords
      });
      if (res?.status?.code === 200) {
        setProducts(res.data?.products || []);
        setPagination(res.data?.pagination || null);
        if (includeKeywords && Array.isArray(res.data?.commonKeywords)) {
          setCommonKeywordsFromApi(res.data.commonKeywords);
        }
      } else if (res?.status?.code === 402) {
        window.location.href = '/login';
      } else {
        message.showToast(res?.error?.message || 'Failed to fetch products', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Error loading products', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag, isParentMode]);

  const fetchExistingMappings = useCallback(async () => {
    try {
      const res = await productListApiService.getKeywordMappingsForTag(tag);
      if (res?.status?.code === 200) {
        const list = res.data?.mappings;
        setExistingMappings(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      message.showToast(err?.message || 'Failed to load saved mappings', ToastTypes.ERROR);
    }
  }, [tag, message]);

  useEffect(() => {
    fetchProducts(currentPage);
  }, [fetchProducts, currentPage]);

  useEffect(() => {
    setCommonKeywordsFromApi(null);
    setCurrentPage(1);
  }, [tag, isParentMode]);

  useEffect(() => {
    fetchExistingMappings();
  }, [fetchExistingMappings]);

  // Preload category tree as soon as page loads so "Map to Category" opens fast (tree is kept cached; not cleared after mapping)
  useEffect(() => {
    productListApiService.getCategoryTreeLight().then((res) => {
      if (res?.status?.code === 200 && Array.isArray(res.data)) setCategoryTree(res.data);
    }).catch(() => {});
  }, []);

  const existingTags = useMemo(() => {
    const s = new Set();
    products.forEach(p => {
      if (Array.isArray(p.keywordSearch)) p.keywordSearch.forEach(k => s.add(k.toLowerCase()));
    });
    return s;
  }, [products]);

  const commonKeywords = useMemo(() => {
    if (commonKeywordsFromApi && commonKeywordsFromApi.length > 0) {
      return commonKeywordsFromApi.map(({ word, count }) => ({
        word,
        count,
        alreadyTag: existingTags.has(word)
      }));
    }
    const wordCount = {};
    products.forEach(p => {
      const words = [...new Set(tokenizeTitle(p.title))];
      words.forEach(w => { wordCount[w] = (wordCount[w] || 0) + 1; });
    });
    return Object.entries(wordCount)
      .map(([word, count]) => ({ word, count, alreadyTag: existingTags.has(word) }))
      .sort((a, b) => b.count - a.count);
  }, [commonKeywordsFromApi, products, existingTags]);

  const toggleKeyword = (word) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(word) ? next.delete(word) : next.add(word);
      return next;
    });
  };

  const selectedArray = [...selected];

  const keywordsCountTwoPlus = useMemo(() => commonKeywords.filter(k => k.count >= 2), [commonKeywords]);
  const keywordsCountOne = useMemo(() => commonKeywords.filter(k => k.count === 1), [commonKeywords]);

  // Get which classification a keyword belongs to (local state)
  const getKeywordClassification = (word) => {
    if (classifiedLocal.important.has(word)) return 'important';
    if (classifiedLocal.normal.has(word)) return 'normal';
    if (classifiedLocal.block.has(word)) return 'block';
    return '';
  };

  const handleClassifyLocal = (classification) => {
    if (selectedArray.length === 0) {
      message.showToast('Select at least one keyword first', ToastTypes.ERROR);
      return;
    }
    setClassifiedLocal(prev => {
      const next = {
        important: new Set(prev.important),
        normal: new Set(prev.normal),
        block: new Set(prev.block),
      };
      // Remove from all groups first
      selectedArray.forEach(w => {
        next.important.delete(w);
        next.normal.delete(w);
        next.block.delete(w);
      });
      // Add to the target group
      selectedArray.forEach(w => next[classification].add(w));
      return next;
    });
    message.showToast(`${selectedArray.length} keyword(s) marked as ${classification}`, ToastTypes.SUCCESS);
    setSelected(new Set());
  };

  const handleRemoveFromClassification = (word) => {
    setClassifiedLocal(prev => {
      const next = {
        important: new Set(prev.important),
        normal: new Set(prev.normal),
        block: new Set(prev.block),
      };
      next.important.delete(word);
      next.normal.delete(word);
      next.block.delete(word);
      return next;
    });
  };

  const hasAnyClassification = classifiedLocal.important.size > 0 || classifiedLocal.normal.size > 0 || classifiedLocal.block.size > 0;

  // Open Map Category modal and load tree
  const loadCategoryTree = async () => {
    setLoadingCategories(true);
    try {
      const res = await productListApiService.getCategoryTreeLight();
      if (res?.status?.code === 200) {
        const tree = res.data || [];
        setCategoryTree(Array.isArray(tree) ? tree : []);
      } else setCategoryTree([]);
    } catch {
      setCategoryTree([]);
    }
    setLoadingCategories(false);
  };

  const handleOpenCategoryPicker = (classification) => {
    setShowCategoryPicker(classification);
    setCategorySearch('');
    setExpandedNodeIds(new Set());
    if (classification !== 'choose' && categoryTree.length === 0) {
      setLoadingCategories(true);
      loadCategoryTree();
    } else {
      setLoadingCategories(false);
    }
  };

  const openCategoryPickerForEdit = () => {
    setShowCategoryPicker('edit');
    setCategorySearch('');
    setExpandedNodeIds(new Set());
    if (categoryTree.length === 0) {
      setLoadingCategories(true);
      loadCategoryTree();
    } else {
      setLoadingCategories(false);
    }
  };

  const flatCategories = useMemo(() => flattenCategoryTree(categoryTree), [categoryTree]);
  const searchQuery = categorySearch.trim().toLowerCase();
  // Whole-word search: only show categories where each search term appears as a full word (not substring like "ring" in "Wringers")
  const filteredBySearch = useMemo(() => {
    if (!searchQuery) return [];
    const tokens = searchQuery.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];
    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const wordRegexes = tokens.map(t => new RegExp(`\\b${escapeRegex(t)}\\b`, 'i'));
    return flatCategories.filter((c) => {
      const text = [c.path, c.c_name].filter(Boolean).join(' ');
      if (!text) return false;
      return wordRegexes.every(re => re.test(text));
    });
  }, [flatCategories, searchQuery]);
  const showSearchResults = searchQuery.length > 0;

  const toggleCategoryExpand = (nodeId) => {
    const id = nodeId?.toString?.() ?? nodeId;
    setExpandedNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMapToCategory = (cat) => {
    if (!showCategoryPicker || showCategoryPicker === 'choose') return;
    if (showCategoryPicker === 'edit') {
      const categoryId = getCategoryId(cat);
      if (categoryId) {
        setEditMapping(prev => prev ? { ...prev, editNewCategoryId: categoryId, editNewCategoryName: cat.path || cat.c_name || '' } : null);
      }
      setShowCategoryPicker(null);
      return;
    }
    const categoryId = getCategoryId(cat);
    if (!categoryId) {
      message.showToast('Invalid category (no ID). Please try another category.', ToastTypes.ERROR);
      return;
    }
    const categoryName = cat.path || cat.c_name || 'category';

    if (showCategoryPicker === 'all') {
      const groups = ['important', 'normal', 'block'].filter(cls => classifiedLocal[cls].size > 0);
      if (groups.length === 0) {
        message.showToast('No classified keywords to map', ToastTypes.ERROR);
        return;
      }
      const totalKeywords = groups.reduce((sum, cls) => sum + classifiedLocal[cls].size, 0);
      const payloads = groups.map(cls => ({ cls, keywords: [...classifiedLocal[cls]] }));
      setShowCategoryPicker(null);
      setClassifiedLocal({ important: new Set(), normal: new Set(), block: new Set() });
      setMappingSaving(true);
      // Sequential calls so each mapping is persisted before the next; parallel would overwrite (same category node)
      (async () => {
        let totalProducts = 0;
        try {
          for (const { cls, keywords } of payloads) {
            const res = await productListApiService.mapKeywordsToCategory(categoryId, tag, cls, keywords);
            if (res?.status?.code !== 200) {
              message.showToast(res?.error?.message || 'Mapping failed', ToastTypes.ERROR);
              return;
            }
            totalProducts += res?.data?.productsUpdated || 0;
          }
          const productMsg = totalProducts > 0 ? ` ${totalProducts} product(s) assigned to category.` : '';
          message.showToast(`All ${totalKeywords} keywords mapped to "${categoryName}".${productMsg}`, ToastTypes.SUCCESS);
          fetchExistingMappings();
          setTimeout(() => fetchExistingMappings(), 600);
        } catch (err) {
          message.showToast(err?.message || 'Error mapping keywords', ToastTypes.ERROR);
        } finally {
          setMappingSaving(false);
        }
      })();
      return;
    }

    const classification = showCategoryPicker;
    const keywords = [...classifiedLocal[classification]];
    if (keywords.length === 0) {
      message.showToast('No keywords in this group', ToastTypes.ERROR);
      return;
    }
    setShowCategoryPicker(null);
    setClassifiedLocal(prev => ({ ...prev, [classification]: new Set() }));
    setMappingSaving(true);
    productListApiService
      .mapKeywordsToCategory(categoryId, tag, classification, keywords)
      .then((res) => {
        if (res?.status?.code === 200) {
          const n = res.data?.productsUpdated;
          const productMsg = n != null && n > 0 ? ` ${n} product(s) assigned to category.` : '';
          message.showToast(`Mapping done: ${keywords.length} ${classification} keywords → "${categoryName}".${productMsg}`, ToastTypes.SUCCESS);
          fetchExistingMappings();
          setTimeout(() => fetchExistingMappings(), 600);
        } else if (res?.status?.code === 402) {
          window.location.href = '/login';
        } else {
          message.showToast(res?.error?.message || 'Mapping failed', ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || 'Error mapping keywords', ToastTypes.ERROR);
      })
      .finally(() => setMappingSaving(false));
  };

  const openEditMapping = (m) => {
    setEditMapping({
      ...m,
      editKeywords: [...(m.keywords || [])],
      editClassification: m.classification,
      editNewCategoryId: null,
      editNewCategoryName: null,
    });
    setEditKeywordInput('');
  };

  const toCategoryIdStr = (id) => {
    if (id == null) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id && typeof id.toString === 'function') return id.toString();
    return String(id);
  };

  const saveEditMapping = () => {
    if (!editMapping) return;
    if (editMapping.editKeywords.length === 0) {
      message.showToast('At least one keyword is required', ToastTypes.ERROR);
      return;
    }
    const categoryId = toCategoryIdStr(editMapping.categoryId);
    const tag = editMapping.tag != null ? String(editMapping.tag).trim() : '';
    const classification = editMapping.classification != null ? String(editMapping.classification).trim() : '';
    if (!categoryId || !tag || !classification) {
      message.showToast('Missing mapping info. Please close and try editing again, or re-map the category.', ToastTypes.ERROR);
      return;
    }
    const payload = {
      newKeywords: editMapping.editKeywords,
      newClassification: editMapping.editClassification,
    };
    if (editMapping.editNewCategoryId) {
      payload.newCategoryId = toCategoryIdStr(editMapping.editNewCategoryId);
    }
    // Close modal immediately; run save in background with spinner
    setEditMapping(null);
    setMappingSaving(true);
    productListApiService
      .updateKeywordMapping(categoryId, tag, classification, payload)
      .then((res) => {
        if (res?.status?.code === 200) {
          const n = res.data?.productsUpdated;
          const productMsg = n != null && n > 0 ? ` ${n} product(s) assigned to category.` : '';
          message.showToast(`Mapping updated.${productMsg}`, ToastTypes.SUCCESS);
          fetchExistingMappings();
        } else if (res?.status?.code === 402) {
          window.location.href = '/login';
        } else {
          message.showToast(res?.error?.message || 'Failed to update mapping', ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || 'Error updating mapping', ToastTypes.ERROR);
      })
      .finally(() => {
        setMappingSaving(false);
      });
  };

  const removeEditKeyword = (kw) => {
    setEditMapping(prev => prev ? { ...prev, editKeywords: prev.editKeywords.filter(k => k !== kw) } : null);
  };

  const addEditKeyword = () => {
    const w = editKeywordInput.trim().toLowerCase();
    if (!w || !editMapping) return;
    if (editMapping.editKeywords.includes(w)) {
      setEditKeywordInput('');
      return;
    }
    setEditMapping(prev => prev ? { ...prev, editKeywords: [...prev.editKeywords, w] } : null);
    setEditKeywordInput('');
  };

  const addEditKeywordByValue = (w) => {
    const word = (w || '').trim().toLowerCase();
    if (!word || !editMapping) return;
    if (editMapping.editKeywords.includes(word)) return;
    setEditMapping(prev => prev ? { ...prev, editKeywords: [...prev.editKeywords, word] } : null);
  };

  const handleDeleteMapping = async (m) => {
    if (!window.confirm(`Remove this mapping from "${m.categoryName}"?`)) return;
    setMappingSaving(true);
    try {
      const res = await productListApiService.deleteKeywordMapping(toCategoryIdStr(m.categoryId), m.tag, m.classification);
      if (res?.status?.code === 200) {
        message.showToast('Mapping removed', ToastTypes.SUCCESS);
        fetchExistingMappings();
      } else if (res?.status?.code === 402) {
        window.location.href = '/login';
      } else {
        message.showToast(res?.error?.message || 'Failed to remove mapping', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Error removing mapping', ToastTypes.ERROR);
    } finally {
      setMappingSaving(false);
    }
  };

  const handleMakeNewTags = async () => {
    if (selectedArray.length === 0) {
      message.showToast('Select at least one keyword first', ToastTypes.ERROR);
      return;
    }
    const tagsToAdd = showCustomInput && customTagName.trim() ? [customTagName.trim()] : selectedArray;
    const allProductIds = products.map(p => p._id);
    setSaving(true);
    try {
      const res = await productListApiService.bulkAddTagToProducts(tagsToAdd, allProductIds);
      if (res?.status?.code === 200) {
        message.showToast(`${res.data?.newTags?.length || tagsToAdd.length} tag(s) added to ${res.data?.updatedCount} products`, ToastTypes.SUCCESS);
        setSelected(new Set());
        setCustomTagName('');
        setShowCustomInput(false);
        fetchProducts(currentPage);
      } else if (res?.status?.code === 402) {
        window.location.href = '/login';
      } else {
        message.showToast(res?.error?.message || 'Failed to add tags', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Error adding tags', ToastTypes.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignToProducts = async () => {
    if (selectedArray.length === 0) {
      message.showToast('Select at least one keyword first', ToastTypes.ERROR);
      return;
    }
    const tagsToAdd = showCustomInput && customTagName.trim() ? [customTagName.trim()] : selectedArray;
    const allProductIds = products.map(p => p._id);
    setSaving(true);
    try {
      const res = await productListApiService.bulkAddTagToProducts(tagsToAdd, allProductIds);
      if (res?.status?.code === 200) {
        message.showToast(`Keywords assigned to ${res.data?.updatedCount} products`, ToastTypes.SUCCESS);
        setSelected(new Set());
        setCustomTagName('');
        setShowCustomInput(false);
        fetchProducts(currentPage);
      } else if (res?.status?.code === 402) {
        window.location.href = '/login';
      } else {
        message.showToast(res?.error?.message || 'Failed to assign', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Error assigning', ToastTypes.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const getProductImage = (prod) => {
    if (prod.image) return prod.image;
    if (Array.isArray(prod.imgUrl) && prod.imgUrl.length > 0) return prod.imgUrl[0];
    return null;
  };

  const renderKeywordChip = ({ word, count, alreadyTag }) => {
    const isSelected = selected.has(word);
    const cls = getKeywordClassification(word);
    return (
      <button
        key={word}
        onClick={() => toggleKeyword(word)}
        className={`tp-kw-chip ${alreadyTag ? 'tp-kw-existing' : ''} ${isSelected ? 'tp-kw-selected' : ''} ${cls ? `tp-kw-cls-${cls}` : ''}`}
        title={cls ? `${word} — ${cls}` : (alreadyTag ? `"${word}" is already a tag` : `Select "${word}"`)}
      >
        {word} ({count})
        {alreadyTag && !isSelected && !cls && <FaCheck className="tp-kw-check" />}
        {isSelected && <FaCheck className="tp-kw-check tp-kw-check-selected" />}
      </button>
    );
  };

  return (
    <div className="tag-products-page">
      {/* Page Header */}
      <div className="tp-page-header">
        <button className="tp-back-btn" onClick={() => window.history.back()}>
          <FaArrowLeft /> Back
        </button>
        <div className="tp-page-title">
          <FaTag className="tp-tag-icon" />
          <h1>Tag: <span className="tp-tag-highlight">"{tag}"</span></h1>
          {isParentMode && (
            <span className="tp-parent-badge" title="Keywords and products include parent + all child tags">
              Parent (includes all child tags)
            </span>
          )}
        </div>
      </div>

      {/* Common Keywords Panel */}
      <div className="tp-keywords-panel">
        <div className="tp-keywords-panel-header">
          <span className="tp-keywords-panel-title">
            Common Keywords ({commonKeywords.length})
            {keywordsCountTwoPlus.length > 0 && keywordsCountOne.length > 0 && (
              <span className="tp-keywords-subtitle">
                {' '}({keywordsCountTwoPlus.length} with count ≥ 2, {keywordsCountOne.length} with count 1)
              </span>
            )}
          </span>
          {selectedArray.length > 0 && (
            <span className="tp-selected-count">{selectedArray.length} selected</span>
          )}
          {selectedArray.length > 0 && (
            <button className="tp-clear-btn" onClick={() => setSelected(new Set())}>Clear all</button>
          )}
        </div>

        <div className="tp-keywords-cloud">
          {loading && products.length === 0 ? (
            <span className="tp-loading-keywords">Loading keywords...</span>
          ) : commonKeywords.length === 0 ? (
            <span className="tp-no-keywords">No keywords extracted</span>
          ) : (
            <>
              {keywordsCountTwoPlus.map(renderKeywordChip)}
              {showCountOneKeywords && keywordsCountOne.map(renderKeywordChip)}
              {keywordsCountOne.length > 0 && (
                <span
                  className="tp-show-more-text"
                  onClick={() => setShowCountOneKeywords(prev => !prev)}
                >
                  {showCountOneKeywords ? 'Show less...' : `Show more...`}
                </span>
              )}
            </>
          )}
        </div>

        {/* Classify Bar - Always visible */}
        <div className="tp-classify-bar">
          <div className="tp-classify-bar-left">
            <span className="tp-classify-bar-label">Classify as:</span>
            <button
              className="tp-classify-btn tp-classify-important"
              onClick={() => handleClassifyLocal('important')}
              disabled={selectedArray.length === 0}
            >
              <FaStar /> Important
            </button>
            <button
              className="tp-classify-btn tp-classify-normal"
              onClick={() => handleClassifyLocal('normal')}
              disabled={selectedArray.length === 0}
            >
              <FaCircle /> Normal
            </button>
            <button
              className="tp-classify-btn tp-classify-block"
              onClick={() => handleClassifyLocal('block')}
              disabled={selectedArray.length === 0}
            >
              <FaBan /> Block
            </button>
          </div>

          <div className="tp-classify-bar-right">
            {showCustomInput ? (
              <div className="tp-custom-input-wrap">
                <input
                  type="text"
                  className="tp-custom-input"
                  placeholder="Enter custom tag name..."
                  value={customTagName}
                  onChange={e => setCustomTagName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleMakeNewTags()}
                  autoFocus
                />
                <button className="tp-cancel-custom" onClick={() => { setShowCustomInput(false); setCustomTagName(''); }}>Cancel</button>
              </div>
            ) : (
              <button
                className="tp-custom-toggle-btn"
                onClick={() => setShowCustomInput(true)}
                title="Use a custom tag name instead of the selected words"
                disabled={selectedArray.length === 0}
              >
                <FaPlus /> Custom name
              </button>
            )}
            <button className="tp-assign-btn" onClick={handleAssignToProducts} disabled={saving || selectedArray.length === 0}>
              {saving ? 'Saving...' : `Assign to ${pagination?.totalProducts || products.length} Products`}
            </button>
          </div>
        </div>

        {/* Selected keywords preview */}
        {selectedArray.length > 0 && (
          <div className="tp-selected-bar">
            <span className="tp-action-label">Selected ({selectedArray.length}):</span>
            <div className="tp-selected-preview">
              {selectedArray.map(w => {
                const cls = getKeywordClassification(w);
                return (
                  <span key={w} className={`tp-selected-tag ${cls ? `tp-sel-${cls}` : ''}`}>
                    {w}
                    <button className="tp-remove-tag" onClick={() => toggleKeyword(w)}>×</button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Classified Keywords Box with Map Category button in header */}
      {hasAnyClassification && (
        <div className="tp-classification-box">
          <div className="tp-cls-box-header">
            <span className="tp-cls-box-title">Classified Keywords</span>
            <button
              type="button"
              className="tp-map-category-btn tp-map-category-compact"
              onClick={() => handleOpenCategoryPicker('all')}
              title="Map all classified keywords to one category"
            >
              <FaFolderOpen /> Map Category
            </button>
          </div>
          <div className="tp-cls-groups">
            {['important', 'normal', 'block'].map(cls => {
              const keywords = [...classifiedLocal[cls]];
              if (keywords.length === 0) return null;
              const config = CLASSIFICATION_CONFIG[cls];
              const Icon = config.icon;
              return (
                <div key={cls} className="tp-cls-group">
                  <div className="tp-cls-group-row tp-cls-group-row-no-btn">
                    <div className="tp-cls-group-left">
                      <div className={`tp-cls-group-header tp-cls-hdr-${cls}`}>
                        <Icon /> {config.label} ({keywords.length})
                      </div>
                      <div className="tp-cls-group-chips">
                        {keywords.map(kw => (
                          <span key={kw} className={`tp-cls-chip tp-cls-chip-${cls}`}>
                            {kw}
                            <button className="tp-cls-chip-remove" onClick={() => handleRemoveFromClassification(kw)}><FaTimes /></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Background mapping indicator - visible while mapping runs after "Map here" */}
      {mappingSaving && (
        <div className="tp-mapping-background-indicator" aria-live="polite">
          <span className="tp-mapping-spinner" />
          <span className="tp-mapping-indicator-text">Mapping in progress…</span>
        </div>
      )}

      {/* Map Category Modal: one flow = choose classification then choose category */}
      {showCategoryPicker && (
        <div className="tp-cat-modal-overlay tp-cat-modal-overlay-on-top" onClick={() => setShowCategoryPicker(null)}>
          <div className="tp-cat-modal" onClick={e => e.stopPropagation()}>
            <div className="tp-cat-modal-header">
              <div className="tp-cat-modal-header-left">
                <h3 className="tp-cat-modal-title">
                  {showCategoryPicker === 'edit' ? 'Change category' : 'Map to Category'}
                </h3>
              </div>
              <button
                type="button"
                className="tp-cat-modal-close"
                onClick={() => setShowCategoryPicker(null)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="tp-cat-modal-body">
              {showCategoryPicker === 'all' && (
                <p className="tp-cat-modal-all-hint">All classified keywords (Important, Normal, Block) will be mapped to the same category.</p>
              )}
              {loadingCategories ? (
                <div className="tp-cat-modal-loading">Loading categories...</div>
              ) : categoryTree.length === 0 ? (
                <div className="tp-cat-modal-empty">No categories found</div>
              ) : (
                <>
                  {/* Search box - always visible at top when tree is loaded */}
                  <div className="tp-cat-search-wrap">
                    <FaSearch className="tp-cat-search-icon" />
                    <input
                      type="text"
                      className="tp-cat-search-input"
                      placeholder="Search categories (optional)..."
                      value={categorySearch}
                      onChange={e => setCategorySearch(e.target.value)}
                    />
                  </div>

                  {showSearchResults ? (
                    <div className="tp-cat-search-results">
                      <p className="tp-cat-results-label">Search results</p>
                      {filteredBySearch.length === 0 ? (
                        <div className="tp-cat-modal-empty">No categories match &quot;{categorySearch}&quot;</div>
                      ) : (
                        filteredBySearch.slice(0, 80).map((cat) => (
                          <div key={cat._id} className="tp-cat-search-row">
                            <span className="tp-cat-search-path">{cat.path}</span>
                            <button
                              type="button"
                              className="tp-cat-tree-map-btn"
                              onClick={() => handleMapToCategory(cat)}
                            >
                              Map here
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="tp-cat-modal-hint">
                        <strong>Expand</strong> — show subcategories. <strong>Map here</strong> — map to this category.
                      </p>
                      <div className="tp-cat-tree">
                        {categoryTree.filter(n => !n.hide).map((node) => (
                          <CategoryTreeRow
                            key={node._id}
                            node={node}
                            depth={0}
                            parentPath=""
                            expandedIds={expandedNodeIds}
                            onToggle={toggleCategoryExpand}
                            onSelect={handleMapToCategory}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Mapping Modal */}
      {editMapping && (
        <div className="tp-cat-modal-overlay" onClick={() => !mappingSaving && setEditMapping(null)}>
          <div className="tp-cat-modal tp-edit-mapping-modal" onClick={e => e.stopPropagation()}>
            <div className="tp-cat-modal-header">
              <h3 className="tp-cat-modal-title">Edit mapping</h3>
              <button type="button" className="tp-cat-modal-close" onClick={() => !mappingSaving && setEditMapping(null)}><FaTimes /></button>
            </div>
            <div className="tp-cat-modal-body">
              <div className="tp-edit-field">
                <span className="tp-edit-label">Classification</span>
                <div className="tp-edit-classify-btns">
                  {['important', 'normal', 'block'].map(cls => {
                    const Cfg = CLASSIFICATION_CONFIG[cls];
                    const Ico = Cfg.icon;
                    return (
                      <button
                        key={cls}
                        type="button"
                        className={`tp-classify-btn tp-classify-${cls} ${editMapping.editClassification === cls ? 'tp-classify-selected' : ''}`}
                        onClick={() => setEditMapping(prev => prev ? { ...prev, editClassification: cls } : null)}
                      >
                        <Ico /> {Cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="tp-edit-field">
                <span className="tp-edit-label">Category</span>
                <div className="tp-edit-category-row">
                  <span className="tp-edit-category-name">
                    <FaFolderOpen /> {editMapping.editNewCategoryName || editMapping.categoryName}
                  </span>
                  <button type="button" className="tp-map-category-btn tp-edit-change-cat" onClick={openCategoryPickerForEdit}>
                    Change category
                  </button>
                </div>
              </div>
              <div className="tp-edit-field">
                <span className="tp-edit-label">Keywords</span>
                <div className="tp-edit-keywords-chips">
                  {editMapping.editKeywords.map(kw => (
                    <span key={kw} className={`tp-cls-chip tp-cls-chip-${editMapping.editClassification}`}>
                      {kw}
                      <button type="button" className="tp-cls-chip-remove" onClick={() => removeEditKeyword(kw)}><FaTimes /></button>
                    </span>
                  ))}
                </div>
                <div className="tp-edit-keyword-add">
                  <input
                    type="text"
                    className="tp-cat-search-input"
                    placeholder="Add keyword..."
                    value={editKeywordInput}
                    onChange={e => setEditKeywordInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEditKeyword(); } }}
                  />
                  <button type="button" className="tp-map-category-btn" onClick={addEditKeyword}>Add</button>
                </div>
                {keywordsCountTwoPlus.length > 0 && (
                  <div className="tp-edit-common-keywords">
                    <span className="tp-edit-common-label">Common keywords (count ≥ 2) — click to add:</span>
                    <div className="tp-edit-common-chips">
                      {keywordsCountTwoPlus.map(({ word, count }) => {
                        const added = editMapping.editKeywords.includes(word.toLowerCase());
                        return (
                          <button
                            key={word}
                            type="button"
                            className={`tp-edit-common-chip ${added ? 'tp-edit-common-chip-added' : ''}`}
                            onClick={() => !added && addEditKeywordByValue(word)}
                            disabled={added}
                            title={added ? 'Already added' : `Add "${word}" (${count})`}
                          >
                            {word} ({count})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="tp-edit-modal-actions">
                <button type="button" className="tp-edit-cancel-btn" onClick={() => setEditMapping(null)} disabled={mappingSaving}>Cancel</button>
                <button type="button" className="tp-edit-save-btn" onClick={saveEditMapping} disabled={mappingSaving || editMapping.editKeywords.length === 0}>
                  {mappingSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Category Mappings - grouped by category so one card shows all (Important + Normal + Block) */}
      <div className="tp-existing-mappings">
        <div className="tp-mappings-title">Saved Category Mappings</div>
        {existingMappings.length === 0 ? (
          <div className="tp-mappings-empty">No saved mappings for this tag. Classify keywords above and use &quot;Map Category&quot; to add one.</div>
        ) : (
          <div className="tp-mappings-list">
            {(() => {
              const byCategory = {};
              existingMappings.forEach(m => {
                const id = m.categoryId != null ? String(m.categoryId) : (m.categoryName || '');
                if (!byCategory[id]) byCategory[id] = { categoryName: m.categoryName || 'Category', mappings: [] };
                byCategory[id].mappings.push(m);
              });
              const groups = Object.entries(byCategory).map(([categoryId, { categoryName, mappings }]) => ({ categoryId, categoryName, mappings }));
              return groups.map(({ categoryId, categoryName, mappings }) => (
                <div key={categoryId} className="tp-mapping-card tp-mapping-group">
                  <div className="tp-mapping-header">
                    <span className="tp-mapping-category tp-mapping-category-main">
                      <FaFolderOpen /> {categoryName}
                    </span>
                  </div>
                  <div className="tp-mapping-group-rows">
                    {mappings.map((m, idx) => {
                      const config = CLASSIFICATION_CONFIG[m.classification] || CLASSIFICATION_CONFIG.normal;
                      const Icon = config.icon;
                      const kwList = Array.isArray(m.keywords) ? m.keywords : [];
                      return (
                        <div key={idx} className={`tp-mapping-group-row tp-mapping-row-${m.classification}`}>
                          <span className={`tp-mapping-badge tp-mapping-badge-${m.classification}`}>
                            <Icon /> {config.label}
                          </span>
                          <div className="tp-mapping-keywords">
                            {kwList.map(kw => (
                              <span key={kw} className={`tp-mapping-kw tp-mapping-kw-${m.classification}`}>{kw}</span>
                            ))}
                          </div>
                          <div className="tp-mapping-actions">
                            <button type="button" className="tp-mapping-edit-btn" onClick={() => openEditMapping(m)} title="Edit mapping"><FaEdit /></button>
                            <button type="button" className="tp-mapping-delete-btn" onClick={() => handleDeleteMapping(m)} title="Remove mapping"><FaTrash /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="tp-table-section">
        <div className="tp-table-header">
          <span className="tp-table-title">
            Products for tag: <strong>{tag}</strong>
            {pagination && <span className="tp-table-total"> (Total: {pagination.totalProducts})</span>}
          </span>
          {pagination && pagination.totalPages > 1 && (
            <div className="tp-pagination-inline">
              <button className="tp-pg-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
              <span className="tp-pg-info">Page {currentPage} / {pagination.totalPages}</span>
              <button className="tp-pg-btn" disabled={currentPage >= pagination.totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
            </div>
          )}
        </div>

        <div className="tp-table-wrap" style={{ position: 'relative', minHeight: 200 }}>
          <Loader value={loading} variant="overlay" text="Loading products..." />
          {!loading && products.length === 0 ? (
            <div className="tp-empty">No products found for tag "{tag}"</div>
          ) : (
            <table className="tp-table">
              <thead>
                <tr>
                  <th className="tp-th tp-th-sno">SNO</th>
                  <th className="tp-th tp-th-image">IMAGE</th>
                  <th className="tp-th tp-th-sku">SKU</th>
                  <th className="tp-th tp-th-title">PRODUCT TITLE</th>
                </tr>
              </thead>
              <tbody>
                {products.map((prod, idx) => {
                  const imgSrc = getProductImage(prod);
                  return (
                    <tr key={prod._id} className="tp-row">
                      <td className="tp-td tp-td-sno">{(currentPage - 1) * (pagination?.limit ?? productsPerPage) + idx + 1}</td>
                      <td className="tp-td tp-td-image">
                        {imgSrc ? (
                          <img src={imgSrc} alt="" className="tp-product-img" onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div className="tp-no-img">No img</div>
                        )}
                      </td>
                      <td className="tp-td tp-td-sku"><span className="tp-sku-text">{prod.sku}</span></td>
                      <td className="tp-td tp-td-title">
                        <HighlightedTitle
                          title={prod.title}
                          selectedKeywords={selectedArray}
                          existingKeywords={Array.isArray(prod.keywordSearch) ? prod.keywordSearch : []}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagProducts;
